// ServiceRouteMap: visor de recorrido GPX reutilizable, montado con MapLibre
// (sin iframe). Reemplaza el embed de gpx.studio para tener control directo de
// las interacciones (flyTo a un punto, marcadores clicables, popups) y features
// conmutables.
//
// Datos: el GPX del servicio (lambda "GPX Rombo") trae trkpts con lat/lon/ele/
// time; la velocidad se deriva (ver helpers/gpxParse.js). Reutiliza useMapStyles
// (mismos estilos base que TraccarMap) para el switcher de capas base.
//
// Modos de datos (prioridad de arriba a abajo):
//   layers (array) Varias rutas superpuestas (original/sanitizado/reconstruido).
//                  Cada capa: { id, label, color, visible?, replay?, points?, path? }
//                  - points: [{lat,lng}] (polyline5 decodificado, sin tiempo).
//                  - path:   path[] del contrato GRS (con t/speed/heading) -> replay.
//                  Se pintan TODAS las capas visibles a la vez (overlay). Con
//                  toggles de visibilidad. El replay opera sobre la capa que
//                  tenga replay:true (o la primera con tiempos).
//   points (array) Una sola ruta ya decodificada [{lat,lng}]. Prioridad sobre gpxUrl.
//   gpxUrl (string) URL del .gpx (de useServiceGpx). Pinta a partir del GPX.
//
// Props comunes:
//   focusPoint    ({lat,lng,zoom,label}) punto a centrar con flyTo (origen/destino/escala).
//   features      (object)  { route, speed, distanceMarkers, basemaps, elevation, stats, export, replay }
//   distanceStepKm(number)  separación de los marcadores de distancia (default 1).
//   height        (string|number) alto del contenedor (default "100%").
//   onStats       (fn)      callback con las estadísticas parseadas (dist, dur, etc.).
//   onError       (fn)      callback si falla la carga/parseo.
//   onRequestFullscreen (fn) si se pasa, muestra botón de "ampliar" que la invoca.
//   serviceCode   (string)  para nombrar el archivo exportado.
//   emptyFallback (node)    UI a mostrar si no hay datos.
import React, { useEffect, useRef, useState, useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import * as XLSX from "xlsx"
import "maplibre-gl/dist/maplibre-gl.css"
import maplibregl from "maplibre-gl"

import useMapStyles from "../TraccarMap/useMapStyles"
import LoaderRombo from "./LoaderRombo"
import { buildDeviationOverlayPlan, buildDeviationPopupOptions } from "./deviationOverlay"
import { fmtMxDate, fmtMxTime } from "../../helpers/datetime"
import {
  parseGpx,
  pointsToParsed,
  pathToParsed,
  pointsToLineString,
  pointsToSpeedSegments,
  distanceMarkers as computeDistanceMarkers,
} from "../../helpers/gpxParse"

const DEFAULT_FEATURES = {
  route: true,
  speed: false,
  distanceMarkers: false,
  basemaps: true,
  elevation: false,
  stats: true,
  export: true,
  replay: false,
}

// Paleta por defecto para capas que no declaren color propio.
const DEFAULT_LAYER_COLORS = ["#539EF3", "#34C38F", "#F1B44C", "#F46A6A", "#556EE6"]

// Velocidades de reproducción del replay (multiplicadores).
const REPLAY_SPEEDS = [1, 2, 5, 10]

// SVG del marcador de reproducción (vehículo que recorre la ruta).
const replayDotSvg = `
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22">
    <circle cx="11" cy="11" r="9" fill="#556EE6" stroke="#fff" stroke-width="3"/>
  </svg>`

// Marcador resaltado para el punto enfocado (clic en origen/destino de una
// escala o del servicio). Pin destacado con halo pulsante para que se note.
const FOCUS_PULSE_CSS_ID = "svc-focus-pulse-style"
const ensureFocusPulseCss = () => {
  if (typeof document === "undefined") return
  if (document.getElementById(FOCUS_PULSE_CSS_ID)) return
  const style = document.createElement("style")
  style.id = FOCUS_PULSE_CSS_ID
  style.textContent = `
    .svc-focus-marker { position: relative; width: 30px; height: 30px; }
    .svc-focus-marker .svc-focus-dot {
      position: absolute; top: 50%; left: 50%; width: 16px; height: 16px;
      margin: -8px 0 0 -8px; border-radius: 50%; background: #F1734F;
      border: 3px solid #fff; box-shadow: 0 0 0 1px rgba(0,0,0,.25); z-index: 2;
    }
    .svc-focus-marker .svc-focus-pulse {
      position: absolute; top: 50%; left: 50%; width: 16px; height: 16px;
      margin: -8px 0 0 -8px; border-radius: 50%; background: rgba(241,115,79,.5);
      z-index: 1; animation: svc-focus-pulse 1.6s ease-out infinite;
    }
    @keyframes svc-focus-pulse {
      0% { transform: scale(1); opacity: .7; }
      100% { transform: scale(3); opacity: 0; }
    }`
  document.head.appendChild(style)
}
const focusMarkerEl = () => {
  const el = document.createElement("div")
  el.className = "svc-focus-marker"
  el.innerHTML = '<span class="svc-focus-pulse"></span><span class="svc-focus-dot"></span>'
  return el
}

// Colores del gradiente de velocidad alineados a los tokens semánticos del
// design system Rombo (error / warning / success). Clamp a 80 km/h.
const SPEED_COLOR = [
  "interpolate", ["linear"], ["get", "speed"],
  0, "#DB2323",   // error (lento)
  20, "#E8B931",  // warning
  40, "#34C38F",  // success
  80, "#0f9d67",  // success oscuro (rápido)
]

// IDs del modo mono-capa (points/gpxUrl).
const SRC_ROUTE = "svc-route-src"
const SRC_SPEED = "svc-speed-src"
const LYR_ROUTE_BORDER = "svc-route-border"
const LYR_ROUTE = "svc-route-line"
const LYR_SPEED = "svc-speed-line"

// IDs derivados por capa (modo multi-capa).
const srcOf = (id) => `svc-ml-src-${id}`
const borderOf = (id) => `svc-ml-border-${id}`
const lineOf = (id) => `svc-ml-line-${id}`

// IDs del overlay de desviación de dirección (círculo + línea creación/operador).
const SRC_DEV_CIRCLE = "svc-deviation-circle-src"
const LYR_DEV_CIRCLE_FILL = "svc-deviation-circle-fill"
const LYR_DEV_CIRCLE_LINE = "svc-deviation-circle-line"
const SRC_DEV_LINE = "svc-deviation-line-src"
const LYR_DEV_LINE = "svc-deviation-line"

// Colores de los marcadores del overlay de desviación.
const DEV_CREATED_COLOR = "#556EE6"   // punto de CREACIÓN de la escala (azul)
const DEV_OPERATOR_COLOR = "#F1734F"  // punto del OPERADOR (naranja)

// CSS acotado SOLO a `.svc-deviation-popup` (A1): evita que la etiqueta de
// desviación se trunque y recoloca el botón de cerrar. NO toca `.svc-map-popup`
// global (compartida por otros popups del mapa). También estiliza la etiqueta
// de dirección original bajo cada geopunto del overlay (A2).
const DEV_POPUP_CSS_ID = "svc-deviation-popup-style"
const ensureDeviationPopupCss = () => {
  if (typeof document === "undefined") return
  if (document.getElementById(DEV_POPUP_CSS_ID)) return
  const style = document.createElement("style")
  style.id = DEV_POPUP_CSS_ID
  style.textContent = `
    .svc-deviation-popup .maplibregl-popup-content {
      padding: 8px 30px 8px 12px; min-width: 150px;
      border-radius: 6px; box-shadow: 0 2px 8px rgba(0,0,0,.25);
    }
    .svc-deviation-popup .fw-bold { white-space: nowrap; font-size: 13px; line-height: 1.3; }
    .svc-deviation-popup .maplibregl-popup-close-button {
      top: 50%; right: 6px; transform: translateY(-50%);
      width: 18px; height: 18px; line-height: 16px; font-size: 16px;
    }
    .svc-dev-addr-label {
      position: absolute; top: 20px; left: 50%; transform: translateX(-50%);
      max-width: 220px; white-space: nowrap; overflow: hidden;
      text-overflow: ellipsis; font-size: 11px; line-height: 1.2;
      padding: 1px 6px; border-radius: 4px; background: rgba(255,255,255,.92);
      border: 1px solid rgba(0,0,0,.12); box-shadow: 0 1px 2px rgba(0,0,0,.15);
      color: #495057; pointer-events: none;
    }`
  document.head.appendChild(style)
}

// Marcador simple (punto coloreado) para creación/operador del overlay. Si se
// pasa `addrText`, pinta debajo una etiqueta con la dirección original (A2);
// el contenedor es `position:relative` para anclar la etiqueta absoluta.
const devMarkerEl = (color, addrText) => {
  const el = document.createElement("div")
  el.style.position = "relative"
  el.innerHTML = `
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
      <circle cx="9" cy="9" r="6" fill="${color}" stroke="#fff" stroke-width="3"/>
    </svg>`
  el.style.cursor = "pointer"
  if (addrText) {
    const label = document.createElement("div")
    label.className = "svc-dev-addr-label"
    label.textContent = addrText
    label.title = addrText
    el.appendChild(label)
  }
  return el
}

const pinSvg = (color) =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="26" height="34" viewBox="0 0 26 34">
    <path d="M13 0C6 0 0 5.6 0 12.6 0 22 13 34 13 34s13-12 13-21.4C26 5.6 20 0 13 0z" fill="${color}"/>
    <circle cx="13" cy="12.5" r="5" fill="#fff"/>
  </svg>`

// HTML del popup de un punto: lat/lng, fecha/hora y botón copiar.
// El botón usa un data-attr con las coords; el handler de copia se delega a
// nivel de mapa (un solo listener) para no acoplar closures por marker.
const pointPopupHtml = (lat, lng, time, title) => {
  const coords = `${lat.toFixed(6)}, ${lng.toFixed(6)}`
  const when = time
    ? `${fmtMxDate(time)} ${fmtMxTime(time)}`
    : "—"
  return `
    <div class="svc-map-popup">
      ${title ? `<div class="fw-bold mb-1">${title}</div>` : ""}
      <div class="small"><span class="text-muted">Lat/Lng:</span> ${coords}</div>
      <div class="small"><span class="text-muted">Fecha/Hora:</span> ${when}</div>
      <button type="button" class="btn btn-sm btn-light mt-1 svc-copy-btn" data-coords="${coords}">
        <i class="bx bx-copy me-1"></i>Copiar
      </button>
    </div>`
}

// Normaliza las capas de entrada: parsea points|path, asigna color/visibilidad.
const buildLayers = (layers) => {
  if (!Array.isArray(layers)) return []
  return layers
    .map((l, i) => {
      if (!l) return null
      const parsed = Array.isArray(l.path) && l.path.length
        ? pathToParsed(l.path)
        : pointsToParsed(l.points || [])
      if (!parsed.points.length) return null
      return {
        id: l.id || `layer-${i}`,
        label: l.label || l.id || `Capa ${i + 1}`,
        color: l.color || DEFAULT_LAYER_COLORS[i % DEFAULT_LAYER_COLORS.length],
        // Si no se indica, la capa arranca visible.
        visible: l.visible !== false,
        // La capa de replay: explícita, o la primera que tenga tiempos.
        replay: !!l.replay,
        hasTime: parsed.points.some((p) => p.time != null),
        parsed,
      }
    })
    .filter(Boolean)
}

const ServiceRouteMap = ({
  gpxUrl,
  points,
  layers,
  focusPoint,
  deviationOverlay,
  showDeviationAddresses = false,
  features,
  distanceStepKm = 1,
  height = "100%",
  onStats,
  onError,
  onRequestFullscreen,
  serviceCode = "recorrido",
  emptyFallback = null,
}) => {
  const feat = { ...DEFAULT_FEATURES, ...(features || {}) }

  const mapContainer = useRef(null)
  const mapRef = useRef(null)
  const mapReadyRef = useRef(false)
  const parsedRef = useRef(null)
  const markersRef = useRef([])
  const popupRef = useRef(null)
  // Overlay de desviación de dirección: marcadores (creación/operador) + popup
  // con la etiqueta de desviación. Las capas/fuentes GeoJSON se gestionan por id.
  const deviationRef = useRef({ markers: [], popup: null })

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [stats, setStats] = useState(null)

  // --- Multi-capa ------------------------------------------------------------
  const builtLayers = useMemo(() => buildLayers(layers), [layers])
  const isMultiLayer = builtLayers.length > 0
  const prevLayerIdsRef = useRef([])
  // Encuadre inicial: fitBounds solo la PRIMERA vez que se pinta (o cuando
  // cambian los datos de entrada), no al alternar capa activa/visibilidad —
  // así cambiar entre rutas no reinicia el zoom/paneo del usuario.
  const hasFitRef = useRef(false)
  // Visibilidad conmutable por capa (id -> bool). Se siembra desde builtLayers.
  const [layerVisible, setLayerVisible] = useState({})
  useEffect(() => {
    if (!isMultiLayer) return
    setLayerVisible((prev) => {
      const next = {}
      builtLayers.forEach((l) => {
        next[l.id] = l.id in prev ? prev[l.id] : l.visible
      })
      return next
    })
  }, [builtLayers, isMultiLayer])

  // Capa activa seleccionada por el usuario (clic en la leyenda). Determina las
  // estadísticas del panel (distancia/velocidad) y la fuente del replay. Se
  // siembra con la capa marcada replay (o la primera) y se puede cambiar.
  const [activeLayerId, setActiveLayerId] = useState(null)
  useEffect(() => {
    if (!isMultiLayer) { setActiveLayerId(null); return }
    setActiveLayerId((prev) => {
      if (prev && builtLayers.some((l) => l.id === prev)) return prev
      const seed = builtLayers.find((l) => l.replay)
        || builtLayers.find((l) => l.hasTime)
        || builtLayers[0]
      return seed?.id || null
    })
  }, [builtLayers, isMultiLayer])

  // Capa activa resuelta (objeto). Es la base de stats y, si tiene tiempos, del replay.
  const activeLayer = useMemo(
    () => builtLayers.find((l) => l.id === activeLayerId) || null,
    [builtLayers, activeLayerId],
  )

  // Fuente del replay. El recorrido avanza por ÍNDICE de punto, así que una capa
  // es reproducible con solo tener puntos; el tiempo/velocidad (si existen) solo
  // enriquecen la etiqueta. En particular, el `path` de /grs/reconstruction en
  // modo anchors llega sin `t` (time:null) y aun así debe poder reproducirse.
  // Prioridad: capa activa marcada replay -> primera marcada replay -> activa con
  // puntos -> primera con tiempos (compat).
  const hasReplayPoints = (l) => !!l && (l.parsed?.points?.length || 0) > 1
  const replayLayer = useMemo(() => {
    if (!isMultiLayer) return null
    if (activeLayer?.replay && hasReplayPoints(activeLayer)) return activeLayer
    const firstReplay = builtLayers.find((l) => l.replay && hasReplayPoints(l))
    if (firstReplay) return firstReplay
    if (hasReplayPoints(activeLayer)) return activeLayer
    return builtLayers.find((l) => l.hasTime) || null
  }, [builtLayers, isMultiLayer, activeLayer])

  // Replay: índice del punto actual, reproducción y velocidad.
  const [replayIdx, setReplayIdx] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [playSpeed, setPlaySpeed] = useState(1)
  const replayMarkerRef = useRef(null)
  const intervalRef = useRef(null)
  const focusMarkerRef = useRef(null)

  // Esquema de color: dos estilos fijos (sin selector de proveedor):
  //  - claro  -> OpenStreetMap
  //  - oscuro -> Carto Dark Matter (destaca mejor las polylines de colores)
  // Tema por defecto: OSCURO (el usuario puede cambiarlo a claro cuando quiera).
  const styles = useMapStyles()
  const lightStyle = useMemo(
    () => styles.find((s) => s.id === "osm") || styles.find((s) => s.scheme !== "dark") || styles[0],
    [styles],
  )
  const darkStyle = useMemo(
    () => styles.find((s) => s.id === "carto-dark") || styles.find((s) => s.scheme === "dark"),
    [styles],
  )
  const [scheme, setScheme] = useState("dark")
  const schemeRef = useRef(scheme)
  useEffect(() => { schemeRef.current = scheme })
  const styleUrlFor = useCallback(
    (sc) => (sc === "dark" ? darkStyle?.url : lightStyle?.url),
    [darkStyle, lightStyle],
  )

  const featRef = useRef(feat)
  useEffect(() => { featRef.current = feat })
  const layersRef = useRef(builtLayers)
  useEffect(() => { layersRef.current = builtLayers })
  const layerVisibleRef = useRef(layerVisible)
  useEffect(() => { layerVisibleRef.current = layerVisible })
  const replayLayerRef = useRef(replayLayer)
  useEffect(() => { replayLayerRef.current = replayLayer })
  const activeLayerRef = useRef(activeLayer)
  useEffect(() => { activeLayerRef.current = activeLayer })

  // Al cambiar los DATOS de entrada (otra ruta/servicio), permite reencuadrar
  // una vez. No se dispara al alternar capa activa/visibilidad (eso no cambia
  // estas props), por lo que el zoom del usuario se respeta entre rutas.
  useEffect(() => {
    hasFitRef.current = false
  }, [layers, points, gpxUrl])

  // --- Carga de datos: multi-capa / puntos pre-decodificados / GPX remoto ----
  useEffect(() => {
    if (isMultiLayer) {
      setError(null)
      // Las estadísticas del panel reflejan la capa ACTIVA seleccionada.
      const base = activeLayer?.parsed || builtLayers[0]?.parsed || null
      setStats(base)
      onStats?.(base)
      setLoading(false)
      if (mapReadyRef.current) drawRoute()
      return
    }
    if (!points || !points.length) return
    setError(null)
    const parsed = pointsToParsed(points)
    parsedRef.current = parsed.points.length ? parsed : null
    setStats(parsed.points.length ? parsed : null)
    onStats?.(parsed.points.length ? parsed : null)
    setLoading(false)
    if (mapReadyRef.current && parsedRef.current) drawRoute()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, isMultiLayer, builtLayers, activeLayer])

  // --- Carga + parseo del GPX ------------------------------------------------
  useEffect(() => {
    let cancelled = false
    // Si hay multi-capa o puntos pre-decodificados, los otros efectos se encargan.
    if (isMultiLayer) return
    if (points && points.length) return
    if (!gpxUrl) {
      setLoading(false)
      parsedRef.current = null
      return
    }
    setLoading(true)
    setError(null)
    fetch(gpxUrl)
      .then((r) => {
        if (!r.ok) throw new Error(`GPX HTTP ${r.status}`)
        return r.text()
      })
      .then((txt) => {
        if (cancelled) return
        const parsed = parseGpx(txt)
        if (!parsed.points.length) throw new Error("GPX sin puntos")
        parsedRef.current = parsed
        setStats(parsed)
        onStats?.(parsed)
        if (mapReadyRef.current) drawRoute()
        setLoading(false)
      })
      .catch((e) => {
        if (cancelled) return
        parsedRef.current = null
        setError(e.message || "Error al cargar el recorrido")
        onError?.(e)
        setLoading(false)
      })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gpxUrl, points, isMultiLayer])

  const clearMarkers = () => {
    markersRef.current.forEach((m) => m.remove())
    markersRef.current = []
  }

  // Limpia TODO el overlay de desviación (marcadores, popup y capas/fuentes
  // GeoJSON). Defensivo: getLayer/removeLayer + getSource/removeSource.
  const clearDeviationOverlay = () => {
    const map = mapRef.current
    const ref = deviationRef.current
    ref.markers.forEach((m) => { try { m.remove() } catch (_) {} })
    ref.markers = []
    try { ref.popup?.remove() } catch (_) {}
    ref.popup = null
    if (!map) return
    ;[LYR_DEV_CIRCLE_FILL, LYR_DEV_CIRCLE_LINE, LYR_DEV_LINE].forEach((id) => {
      try { if (map.getLayer(id)) map.removeLayer(id) } catch (_) {}
    })
    ;[SRC_DEV_CIRCLE, SRC_DEV_LINE].forEach((id) => {
      try { if (map.getSource(id)) map.removeSource(id) } catch (_) {}
    })
  }

  // Elimina las capas/fuentes del modo mono-capa.
  const removeSingleLayerGfx = (map) => {
    ;[LYR_SPEED, LYR_ROUTE, LYR_ROUTE_BORDER].forEach((id) => {
      try { if (map.getLayer(id)) map.removeLayer(id) } catch (_) {}
    })
    ;[SRC_SPEED, SRC_ROUTE].forEach((id) => {
      try { if (map.getSource(id)) map.removeSource(id) } catch (_) {}
    })
  }

  // Elimina las capas/fuentes del modo multi-capa (por lista de ids).
  const removeMultiLayerGfx = (map, ids) => {
    ids.forEach((id) => {
      ;[lineOf(id), borderOf(id)].forEach((lid) => {
        try { if (map.getLayer(lid)) map.removeLayer(lid) } catch (_) {}
      })
      try { if (map.getSource(srcOf(id))) map.removeSource(srcOf(id)) } catch (_) {}
    })
  }

  // Dibuja los waypoints (origen/destino/escalas) de una estructura parsed.
  const drawWaypoints = (map, parsed) => {
    const colorByKind = { start: "#34C38F", end: "#DB2323", scale: "#539EF3", waypoint: "#74788D" }
    ;(parsed.waypoints || []).forEach((w) => {
      const el = document.createElement("div")
      el.innerHTML = pinSvg(colorByKind[w.kind] || "#616161")
      el.style.cursor = "pointer"
      const marker = new maplibregl.Marker({ element: el, anchor: "bottom" })
        .setLngLat([w.lng, w.lat])
        .addTo(map)
      el.addEventListener("click", () => {
        popupRef.current
          ?.setLngLat([w.lng, w.lat])
          .setHTML(pointPopupHtml(w.lat, w.lng, w.time, w.name || w.kind))
          .addTo(map)
        map.flyTo({ center: [w.lng, w.lat], zoom: Math.max(map.getZoom(), 15), duration: 700 })
      })
      markersRef.current.push(marker)
    })
  }

  const drawRoute = useCallback(() => {
    const map = mapRef.current
    if (!map || !mapReadyRef.current) return
    const f = featRef.current

    // Limpia TODO el grafismo anterior (ambos modos) para re-dibujar limpio.
    removeSingleLayerGfx(map)
    removeMultiLayerGfx(map, prevLayerIdsRef.current)
    clearMarkers()

    // ---- Modo multi-capa: superpone todas las capas visibles ----------------
    if (layersRef.current.length) {
      const ls = layersRef.current
      const vis = layerVisibleRef.current
      prevLayerIdsRef.current = ls.map((l) => l.id)

      let unionBounds = null
      ls.forEach((l) => {
        if (vis[l.id] === false) return
        const pts = l.parsed.points
        if (!pts.length) return
        map.addSource(srcOf(l.id), { type: "geojson", data: pointsToLineString(pts) })
        // Sombra: línea oscura difusa (blur) y más ancha debajo, para dar
        // relieve y distinguir las capas superpuestas sobre fondo claro u oscuro.
        map.addLayer({
          id: borderOf(l.id), type: "line", source: srcOf(l.id),
          layout: { "line-join": "round", "line-cap": "round" },
          paint: {
            "line-color": "#000000",
            "line-width": 9,
            "line-opacity": 0.35,
            "line-blur": 4,
            "line-translate": [1, 2],
          },
        })
        map.addLayer({
          id: lineOf(l.id), type: "line", source: srcOf(l.id),
          layout: { "line-join": "round", "line-cap": "round" },
          paint: { "line-color": l.color, "line-width": 4.5, "line-opacity": 0.98 },
        })
        // Acumula bounds de las capas visibles para encuadrar.
        const b = l.parsed.bounds
        if (b) {
          if (!unionBounds) unionBounds = [[b[0][0], b[0][1]], [b[1][0], b[1][1]]]
          else {
            unionBounds[0][0] = Math.min(unionBounds[0][0], b[0][0])
            unionBounds[0][1] = Math.min(unionBounds[0][1], b[0][1])
            unionBounds[1][0] = Math.max(unionBounds[1][0], b[1][0])
            unionBounds[1][1] = Math.max(unionBounds[1][1], b[1][1])
          }
        }
      })

      // Waypoints + marcadores de distancia: sobre la capa principal = la capa
      // ACTIVA seleccionada (fallback a replay o a la primera visible). Replica
      // la función del modo mono-capa que se perdió al introducir el overlay.
      const mainLayer = activeLayerRef.current
        || replayLayerRef.current
        || ls.find((l) => vis[l.id] !== false)
      const wpSource = mainLayer?.parsed
      if (wpSource) drawWaypoints(map, wpSource)

      if (f.distanceMarkers && wpSource) {
        const dm = computeDistanceMarkers(wpSource.points, distanceStepKm)
        dm.forEach((m) => {
          const el = document.createElement("div")
          el.className = "svc-dist-marker"
          el.textContent = `${m.km}`
          const marker = new maplibregl.Marker({ element: el, anchor: "center" })
            .setLngLat([m.lng, m.lat])
            .addTo(map)
          markersRef.current.push(marker)
        })
      }

      if (!focusPoint && unionBounds && !hasFitRef.current) {
        map.fitBounds(unionBounds, { padding: 50, maxZoom: 16, duration: 600 })
        hasFitRef.current = true
      }
      return
    }

    // ---- Modo mono-capa (points / gpxUrl) -----------------------------------
    const parsed = parsedRef.current
    if (!parsed) return
    prevLayerIdsRef.current = []

    map.addSource(SRC_ROUTE, { type: "geojson", data: pointsToLineString(parsed.points) })

    if (f.route) {
      map.addLayer({
        id: LYR_ROUTE_BORDER, type: "line", source: SRC_ROUTE,
        layout: { "line-join": "round", "line-cap": "round" },
        paint: { "line-color": "#ffffff", "line-width": 7, "line-opacity": 0.7 },
      })
    }

    if (f.speed) {
      map.addSource(SRC_SPEED, { type: "geojson", data: pointsToSpeedSegments(parsed.points) })
      map.addLayer({
        id: LYR_SPEED, type: "line", source: SRC_SPEED,
        layout: { "line-join": "round", "line-cap": "round" },
        paint: { "line-color": SPEED_COLOR, "line-width": 4.5, "line-opacity": 0.95 },
      })
    } else if (f.route) {
      map.addLayer({
        id: LYR_ROUTE, type: "line", source: SRC_ROUTE,
        layout: { "line-join": "round", "line-cap": "round" },
        paint: { "line-color": "#539EF3", "line-width": 4, "line-opacity": 0.95 },
      })
    }

    drawWaypoints(map, parsed)

    if (f.distanceMarkers) {
      const dm = computeDistanceMarkers(parsed.points, distanceStepKm)
      dm.forEach((m) => {
        const el = document.createElement("div")
        el.className = "svc-dist-marker"
        el.textContent = `${m.km}`
        const marker = new maplibregl.Marker({ element: el, anchor: "center" })
          .setLngLat([m.lng, m.lat])
          .addTo(map)
        markersRef.current.push(marker)
      })
    }

    if (!focusPoint && parsed.bounds && !hasFitRef.current) {
      map.fitBounds(parsed.bounds, { padding: 50, maxZoom: 16, duration: 600 })
      hasFitRef.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distanceStepKm, focusPoint])

  // --- Inicialización del mapa ----------------------------------------------
  useEffect(() => {
    if (mapRef.current) return
    const initialUrl = styleUrlFor("dark")
    if (!initialUrl) return

    const map = new maplibregl.Map({
      container: mapContainer.current,
      style: initialUrl,
      center: [-102.5528, 23.6345],
      zoom: 4,
      attributionControl: false,
    })
    mapRef.current = map
    map.addControl(new maplibregl.NavigationControl(), "top-right")

    popupRef.current = new maplibregl.Popup({ offset: 20, closeButton: true, closeOnClick: true })

    // Delegación del botón "copiar" dentro de los popups.
    map.getContainer().addEventListener("click", (ev) => {
      const btn = ev.target.closest?.(".svc-copy-btn")
      if (!btn) return
      const coords = btn.getAttribute("data-coords") || ""
      navigator.clipboard?.writeText(coords).then(() => {
        btn.innerHTML = '<i class="bx bx-check me-1"></i>Copiado'
        setTimeout(() => {
          btn.innerHTML = '<i class="bx bx-copy me-1"></i>Copiar'
        }, 1500)
      }).catch(() => {})
    })

    // Click derecho en cualquier punto del mapa: copia "lat,lng" de ese punto
    // al portapapeles y muestra un popup breve de confirmación. Complementa el
    // botón "Copiar" de los popups de marcadores (que copian el punto exacto).
    map.on("contextmenu", (e) => {
      const coords = `${e.lngLat.lat.toFixed(6)},${e.lngLat.lng.toFixed(6)}`
      navigator.clipboard?.writeText(coords).catch(() => {})
      popupRef.current
        ?.setLngLat(e.lngLat)
        .setHTML(
          `<div class="svc-map-popup small">`
          + `<div><span class="text-muted">Lat/Lng:</span> ${coords}</div>`
          + `<div class="text-success"><i class="bx bx-check me-1"></i>Copiado</div>`
          + `</div>`,
        )
        .addTo(map)
    })

    map.on("styleimagemissing", (e) => {
      const px = new Image(1, 1)
      px.src = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg=="
      px.onload = () => { try { if (!map.hasImage(e.id)) map.addImage(e.id, px) } catch (_) {} }
    })

    map.on("load", () => {
      mapReadyRef.current = true
      drawRoute()
    })

    return () => {
      mapReadyRef.current = false
      clearInterval(intervalRef.current)
      clearMarkers()
      replayMarkerRef.current?.remove()
      replayMarkerRef.current = null
      focusMarkerRef.current?.remove()
      focusMarkerRef.current = null
      clearDeviationOverlay()
      popupRef.current?.remove()
      map.remove()
      mapRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Cambia el estilo base conservando centro/zoom y RE-DIBUJA los trazos. Al
  // hacer setStyle, MapLibre descarta las sources/layers geojson (los trazos),
  // por eso hay que repintarlos cuando el nuevo estilo termina de cargar. Se
  // usa 'style.load' y, como respaldo (algunos estilos no lo emiten de forma
  // fiable), un 'idle' de una sola vez. prevLayerIdsRef se limpia para no
  // intentar remover capas que el nuevo estilo ya no tiene.
  const applyStyle = useCallback((url) => {
    const map = mapRef.current
    if (!map || !url) return
    const center = map.getCenter()
    const zoom = map.getZoom()
    mapReadyRef.current = false
    prevLayerIdsRef.current = []
    let redrawn = false
    const redraw = () => {
      if (redrawn) return
      redrawn = true
      mapReadyRef.current = true
      map.jumpTo({ center, zoom })
      drawRoute()
    }
    map.once("style.load", redraw)
    // Respaldo: si style.load no dispara el redibujo, el primer idle lo hace.
    map.once("idle", redraw)
    map.setStyle(url)
  }, [drawRoute])

  const applySchemeChange = useCallback((sc) => {
    if (sc === schemeRef.current) return
    setScheme(sc)
    applyStyle(styleUrlFor(sc))
  }, [applyStyle, styleUrlFor])

  const currentIsDark = scheme === "dark"

  // Re-dibuja al cambiar features, step, visibilidad o la capa activa (los
  // waypoints/distancia se pintan sobre la capa principal, que sigue a la activa).
  useEffect(() => {
    if (mapReadyRef.current) drawRoute()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feat.route, feat.speed, feat.distanceMarkers, distanceStepKm, layerVisible, builtLayers, activeLayerId])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !focusPoint) return
    const { lat, lng, zoom = 16 } = focusPoint
    if (!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) return
    const lngLat = [Number(lng), Number(lat)]
    const place = () => {
      map.flyTo({ center: lngLat, zoom, duration: 800 })
      // Marcador resaltado persistente en el punto enfocado.
      ensureFocusPulseCss()
      if (!focusMarkerRef.current) {
        focusMarkerRef.current = new maplibregl.Marker({ element: focusMarkerEl(), anchor: "center" })
          .setLngLat(lngLat)
          .addTo(map)
      } else {
        focusMarkerRef.current.setLngLat(lngLat).addTo(map)
      }
    }
    if (mapReadyRef.current) place()
    else map.once("load", place)
  }, [focusPoint])

  // --- Overlay de desviación de dirección ------------------------------------
  // Al recibir `deviationOverlay` (clic en una escala del detalle), pinta:
  //  (1) marcadores de los geopuntos de CREACIÓN y de OPERADOR (si hay),
  //  (2) una línea entre ambos,
  //  (3) un círculo geodésico centrado en el punto de CREACIÓN con radio =
  //      desviación en metros (o la tolerancia si no hay desviación calculable),
  //  (4) un popup con la etiqueta de la desviación.
  // El color del círculo indica si la desviación excede la tolerancia (rojo) o
  // no (verde). Prop opcional: sin ella, el mapa se comporta igual (aditivo) y
  // cualquier overlay previo se limpia.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return undefined

    const draw = () => {
      // Siempre limpia el overlay previo antes de (re)dibujar.
      clearDeviationOverlay()
      // Plan PURO (misma lógica verificada en los tests) de qué pintar.
      const plan = buildDeviationOverlayPlan(deviationOverlay)
      if (!plan) return
      ensureDeviationPopupCss()

      const ref = deviationRef.current
      const { lat: cLat, lng: cLng } = plan.created

      // Etiqueta de dirección original bajo cada geopunto (A2): la dirección
      // textual si existe; si no (operador, que no tiene texto) las coords a 6
      // decimales. Solo cuando el toggle `showDeviationAddresses` está activo.
      const addrLabel = (point) => {
        if (!showDeviationAddresses) return null
        if (point?.address) return point.address
        const lat = Number(point?.lat)
        const lng = Number(point?.lng)
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
        return `${lat.toFixed(6)}, ${lng.toFixed(6)}`
      }

      // Marcador de CREACIÓN.
      ref.markers.push(
        new maplibregl.Marker({ element: devMarkerEl(DEV_CREATED_COLOR, addrLabel(plan.created)), anchor: "center" })
          .setLngLat([cLng, cLat])
          .addTo(map),
      )

      // Marcador de OPERADOR + línea creación->operador (si hay operador).
      if (plan.hasOperator) {
        const { lat: oLat, lng: oLng } = plan.operator
        ref.markers.push(
          new maplibregl.Marker({ element: devMarkerEl(DEV_OPERATOR_COLOR, addrLabel(plan.operator)), anchor: "center" })
            .setLngLat([oLng, oLat])
            .addTo(map),
        )
        map.addSource(SRC_DEV_LINE, {
          type: "geojson",
          data: {
            type: "Feature",
            geometry: { type: "LineString", coordinates: [[cLng, cLat], [oLng, oLat]] },
            properties: {},
          },
        })
        map.addLayer({
          id: LYR_DEV_LINE, type: "line", source: SRC_DEV_LINE,
          layout: { "line-join": "round", "line-cap": "round" },
          paint: {
            "line-color": plan.color,
            "line-width": 2.5,
            "line-dasharray": [2, 2],
            "line-opacity": 0.9,
          },
        })
      }

      // Círculo geodésico centrado en CREACIÓN.
      if (plan.drawsCircle) {
        map.addSource(SRC_DEV_CIRCLE, { type: "geojson", data: plan.circle })
        map.addLayer({
          id: LYR_DEV_CIRCLE_FILL, type: "fill", source: SRC_DEV_CIRCLE,
          paint: { "fill-color": plan.color, "fill-opacity": 0.15 },
        })
        map.addLayer({
          id: LYR_DEV_CIRCLE_LINE, type: "line", source: SRC_DEV_CIRCLE,
          paint: { "line-color": plan.color, "line-width": 2, "line-opacity": 0.9 },
        })
      }

      // Popup con la etiqueta de la desviación (anclado al punto de creación).
      if (plan.label) {
        ref.popup = new maplibregl.Popup(buildDeviationPopupOptions())
          .setLngLat([cLng, cLat])
          .setHTML(`<div class="svc-map-popup"><div class="fw-bold">${plan.label}</div></div>`)
          .addTo(map)
      }

      // Encuadra ambos puntos (o centra en creación si no hay operador).
      if (plan.hasOperator) {
        const { lat: oLat, lng: oLng } = plan.operator
        const bounds = new maplibregl.LngLatBounds([cLng, cLat], [cLng, cLat])
        bounds.extend([oLng, oLat])
        map.fitBounds(bounds, { padding: 90, maxZoom: 17, duration: 700 })
      } else {
        map.flyTo({ center: [cLng, cLat], zoom: Math.max(map.getZoom(), 15), duration: 700 })
      }
    }

    if (mapReadyRef.current) draw()
    else map.once("load", draw)
    return undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviationOverlay, showDeviationAddresses])

  // Puntos activos para el replay: capa de replay (multi) o la ruta mono-capa.
  const replayPoints = useMemo(() => {
    if (isMultiLayer) return replayLayer?.parsed?.points || []
    return parsedRef.current?.points || (stats?.points || [])
    // stats refleja el parsed del modo mono-capa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMultiLayer, replayLayer, stats])

  // Al cambiar la fuente del replay, reinicia el cursor.
  useEffect(() => {
    setPlaying(false)
    setReplayIdx(0)
  }, [replayLayer, isMultiLayer])

  // --- Replay: avanza el índice mientras playing (basado en TraccarMap) ------
  useEffect(() => {
    clearInterval(intervalRef.current)
    const pts = replayPoints
    if (!playing || !pts.length) return
    intervalRef.current = setInterval(() => {
      setReplayIdx((prev) => {
        if (prev >= pts.length - 1) {
          setPlaying(false)
          return prev
        }
        return prev + 1
      })
    }, Math.max(50, 300 / playSpeed))
    return () => clearInterval(intervalRef.current)
  }, [playing, playSpeed, replayPoints])

  // Mueve el marcador de replay al punto actual y centra suavemente el mapa.
  useEffect(() => {
    const map = mapRef.current
    const pts = replayPoints
    if (!map || !mapReadyRef.current || !pts.length) return
    if (!featRef.current.replay) return
    const p = pts[Math.min(replayIdx, pts.length - 1)]
    if (!p) return
    if (!replayMarkerRef.current) {
      const el = document.createElement("div")
      el.innerHTML = replayDotSvg
      replayMarkerRef.current = new maplibregl.Marker({ element: el, anchor: "center" })
        .setLngLat([p.lng, p.lat])
        .addTo(map)
    } else {
      replayMarkerRef.current.setLngLat([p.lng, p.lat])
    }
    if (playing) map.panTo([p.lng, p.lat], { duration: 200 })
  }, [replayIdx, playing, replayPoints])

  // --- Exportar la ruta a Excel: ID incremental, lat, lng, fecha, hora -------
  const exportExcel = useCallback(() => {
    const parsed = isMultiLayer
      ? (replayLayerRef.current?.parsed || layersRef.current[0]?.parsed)
      : parsedRef.current
    if (!parsed || !parsed.points.length) return
    const rows = parsed.points.map((p, i) => ({
      ID: i + 1,
      Latitud: p.lat,
      Longitud: p.lng,
      Fecha: p.time ? fmtMxDate(p.time) : "",
      Hora: p.time ? fmtMxTime(p.time) : "",
    }))
    const ws = XLSX.utils.json_to_sheet(rows, {
      header: ["ID", "Latitud", "Longitud", "Fecha", "Hora"],
    })
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Recorrido")
    XLSX.writeFile(wb, `${serviceCode}_recorrido.xlsx`)
  }, [serviceCode, isMultiLayer])

  const toggleLayer = (id) =>
    setLayerVisible((prev) => ({ ...prev, [id]: !(prev[id] !== false) }))

  const hasData = isMultiLayer || (points && points.length) || gpxUrl
  if (!hasData) return emptyFallback

  // ¿Hay controles de replay disponibles? (mono-capa feat.replay, o multi con capa de tiempos)
  const replayAvailable = feat.replay && replayPoints.length > 1

  return (
    <div style={{ position: "relative", width: "100%", height }}>
      <div ref={mapContainer} style={{ position: "absolute", inset: 0, borderRadius: 8, overflow: "hidden" }} />

      {loading && (
        <div style={{
          position: "absolute", inset: 0, display: "flex", alignItems: "center",
          justifyContent: "center", background: "rgba(255,255,255,.6)", zIndex: 2,
        }}>
          <LoaderRombo />
        </div>
      )}

      {error && !loading && (
        <div style={{
          position: "absolute", inset: 0, display: "flex", alignItems: "center",
          justifyContent: "center", background: "#F8F9FA", zIndex: 2,
        }}>
          <span className="text-muted"><i className="bx bx-error-circle me-1" />{error}</span>
        </div>
      )}

      {/* Barra superior de acciones: exportar + ampliar. */}
      {!loading && !error && (feat.export || onRequestFullscreen) && (
        <div style={{ position: "absolute", top: 8, left: 8, zIndex: 3 }} className="d-flex gap-1">
          {feat.export && (
            <button type="button" className="btn btn-sm btn-light shadow-sm" onClick={exportExcel}
              title="Exportar recorrido a Excel">
              <i className="bx bx-download" />
            </button>
          )}
          {onRequestFullscreen && (
            <button type="button" className="btn btn-sm btn-light shadow-sm" onClick={onRequestFullscreen}
              title="Ampliar mapa">
              <i className="bx bx-fullscreen" />
            </button>
          )}
        </div>
      )}

      {/* Leyenda de capas (modo multi-capa). Clic en el nombre/color: selecciona
          la capa ACTIVA (stats + replay la siguen). Ícono ojo: visibilidad. */}
      {isMultiLayer && !loading && !error && (
        <div style={{
          position: "absolute", top: 8, right: 48, zIndex: 3,
          background: "rgba(255,255,255,.95)", borderRadius: 6, padding: "6px 8px",
          fontSize: 11, boxShadow: "0 1px 4px rgba(0,0,0,.2)", maxWidth: 210,
        }}>
          {builtLayers.map((l) => {
            const on = layerVisible[l.id] !== false
            const isActive = l.id === activeLayerId
            return (
              <div key={l.id} className="d-flex align-items-center gap-1 py-1"
                style={{ opacity: on ? 1 : 0.5 }}>
                <button type="button"
                  onClick={() => setActiveLayerId(l.id)}
                  className="d-flex align-items-center gap-2 flex-grow-1 border-0 bg-transparent px-0 text-start"
                  style={{ cursor: "pointer" }}
                  title={isActive ? "Capa activa (distancia/velocidad)" : "Ver distancia/velocidad de esta capa"}>
                  <span style={{
                    width: 14, height: 4, borderRadius: 2, background: l.color,
                    display: "inline-block", flexShrink: 0,
                  }} />
                  <span className={`text-truncate ${isActive ? "fw-bold" : ""}`}>{l.label}</span>
                  {isActive && <i className="bx bx-check text-primary" />}
                </button>
                <button type="button"
                  onClick={() => toggleLayer(l.id)}
                  className="border-0 bg-transparent px-1"
                  style={{ cursor: "pointer" }}
                  title={on ? "Ocultar capa" : "Mostrar capa"}>
                  <i className={`bx ${on ? "bx-show" : "bx-hide"}`} />
                </button>
              </div>
            )
          })}
        </div>
      )}

      {/* Panel de estadísticas del recorrido (distancia / duración / velocidad).
          En multi-capa refleja la capa principal (replay/primera) y se coloca
          bajo la leyenda de capas para no encimarse. */}
      {feat.stats && stats && !loading && !error && (
        <div style={{
          position: "absolute", zIndex: 3,
          // Mono-capa: arriba-derecha. Multi-capa: abajo-derecha (arriba lo usan
          // los botones y la leyenda de capas; así no se traslapa con el tema).
          // Sube por encima del replay (offset mayor) para no quedar tapado.
          ...(isMultiLayer
            ? { bottom: replayAvailable ? 76 : 8, right: 8, zIndex: 5 }
            : { top: 8, right: 48 }),
          background: "rgba(255,255,255,.92)", borderRadius: 6, padding: "4px 8px",
          fontSize: 11, boxShadow: "0 1px 4px rgba(0,0,0,.2)",
        }}>
          <span className="me-2"><i className="bx bx-ruler me-1" />{(stats.totalDistance / 1000).toFixed(2)} km</span>
          {stats.avgSpeed != null && (
            <span><i className="bx bx-tachometer me-1" />{stats.avgSpeed.toFixed(0)} km/h</span>
          )}
        </div>
      )}

      {/* Switcher de esquema claro/oscuro (sustituye al antiguo selector de
          proveedor; misma posición abajo-izquierda). Claro=OSM, Oscuro=Carto.
          z-index por encima del replay y offset mayor para no quedar tapado. */}
      {feat.basemaps && darkStyle && !loading && !error && (
        <div style={{ position: "absolute", bottom: replayAvailable ? 76 : 8, left: 8, zIndex: 5 }}>
          <div className="btn-group btn-group-sm shadow-sm" role="group" aria-label="Esquema de color">
            <button type="button"
              className={`btn btn-sm ${currentIsDark ? "btn-light" : "btn-primary"}`}
              onClick={() => applySchemeChange("light")} title="Mapa claro (OSM)">
              <i className="bx bx-sun" />
            </button>
            <button type="button"
              className={`btn btn-sm ${currentIsDark ? "btn-primary" : "btn-light"}`}
              onClick={() => applySchemeChange("dark")} title="Mapa oscuro (Carto)">
              <i className="bx bx-moon" />
            </button>
          </div>
        </div>
      )}

      {feat.speed && !isMultiLayer && !loading && !error && (
        <div style={{
          position: "absolute", bottom: replayAvailable ? 60 : 8, right: 8, zIndex: 3,
          background: "rgba(255,255,255,.9)", borderRadius: 6, padding: "4px 8px",
          fontSize: 11, boxShadow: "0 1px 4px rgba(0,0,0,.2)",
        }}>
          <div className="d-flex align-items-center gap-1">
            <span style={{ width: 10, height: 10, background: "#DB2323", display: "inline-block", borderRadius: 2 }} />
            <span className="me-1">lento</span>
            <span style={{ width: 10, height: 10, background: "#34C38F", display: "inline-block", borderRadius: 2 }} />
            <span>rápido</span>
          </div>
        </div>
      )}

      {/* Controles de replay (recorre los trackpoints; basado en TraccarMap). */}
      {replayAvailable && !loading && !error && (
        <div style={{
          position: "absolute", bottom: 8, left: 8, right: 8, zIndex: 4,
          background: "rgba(255,255,255,.95)", borderRadius: 6, padding: "6px 10px",
          boxShadow: "0 1px 4px rgba(0,0,0,.25)",
        }}>
          <div className="d-flex align-items-center gap-2">
            <button type="button" className="btn btn-sm btn-light"
              onClick={() => { setPlaying(false); setReplayIdx(0) }} title="Inicio">
              <i className="bx bx-skip-previous" />
            </button>
            <button type="button" className="btn btn-sm btn-primary"
              disabled={replayIdx >= replayPoints.length - 1 && !playing}
              onClick={() => setPlaying((p) => !p)} title={playing ? "Pausar" : "Reproducir"}>
              <i className={`bx ${playing ? "bx-pause" : "bx-play"}`} />
            </button>
            <input type="range" className="form-range flex-grow-1" min={0}
              max={replayPoints.length - 1} value={replayIdx}
              onChange={(e) => { setPlaying(false); setReplayIdx(Number(e.target.value)) }} />
            <button type="button" className="btn btn-sm btn-light"
              onClick={() => setPlaySpeed((s) => REPLAY_SPEEDS[(REPLAY_SPEEDS.indexOf(s) + 1) % REPLAY_SPEEDS.length])}
              title="Velocidad" style={{ minWidth: 42 }}>
              {playSpeed}x
            </button>
          </div>
          <div className="text-muted small text-center mt-1">
            {isMultiLayer && replayLayer ? `${replayLayer.label} · ` : ""}
            {replayIdx + 1}/{replayPoints.length}
            {replayPoints[replayIdx]?.time
              ? ` · ${fmtMxDate(replayPoints[replayIdx].time)} ${fmtMxTime(replayPoints[replayIdx].time)}`
              : ""}
            {replayPoints[replayIdx]?.speed != null
              ? ` · ${replayPoints[replayIdx].speed.toFixed(0)} km/h`
              : ""}
          </div>
        </div>
      )}
    </div>
  )
}

ServiceRouteMap.propTypes = {
  gpxUrl: PropTypes.string,
  points: PropTypes.arrayOf(PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  })),
  layers: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    color: PropTypes.string,
    visible: PropTypes.bool,
    replay: PropTypes.bool,
    points: PropTypes.array,
    path: PropTypes.array,
  })),
  focusPoint: PropTypes.object,
  deviationOverlay: PropTypes.shape({
    created: PropTypes.shape({ lat: PropTypes.number, lng: PropTypes.number, address: PropTypes.string }),
    operator: PropTypes.shape({ lat: PropTypes.number, lng: PropTypes.number, address: PropTypes.string }),
    deviationM: PropTypes.number,
    toleranceM: PropTypes.number,
    label: PropTypes.string,
  }),
  showDeviationAddresses: PropTypes.bool,
  features: PropTypes.object,
  distanceStepKm: PropTypes.number,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onStats: PropTypes.func,
  onError: PropTypes.func,
  onRequestFullscreen: PropTypes.func,
  serviceCode: PropTypes.string,
  emptyFallback: PropTypes.node,
}

export default ServiceRouteMap
